import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  IUnlockEligibleRecord,
  UnlockListState,
  UnlockStoreScope,
} from "@/@types/unlock-request";
import { SituationCell } from "@/components/unlock-request/situation-cell";
import { UNLOCK_COMMENT_MAX_LENGTH } from "@/constants/access-request";
import {
  UNLOCK_ENTITY_LABEL,
  UNLOCK_MODAL_LABELS,
  UNLOCK_TAB_LABELS,
} from "@/constants/messages";
import {
  UNLOCK_CONFIRM_TEST_ID,
  UNLOCK_ENTITY_TYPES,
  UNLOCK_LIST_STATE,
  UNLOCK_LIST_TEST_ID,
  UNLOCK_MODAL_TEST_ID,
  UNLOCK_SELECTED_TEST_ID,
  UNLOCK_STORE_SCOPE,
} from "@/constants/unlock-request";
import { UnlockEntityTypeEnum } from "@/enum/access_request";
import {
  isUnlockReleasable,
  keepSearchFromSubmitting,
  unlockRecordKey,
  unlockRecordName,
} from "@/utils/unlock-request";

import { SelectInput } from "components/Select";

import { useUnlockRequestForm } from "./use-unlock-request-form";

const Hint = ({ text }: { text: string }): JSX.Element => (
  <p className="p-4 text-sm text-muted-foreground">{text}</p>
);

const ErrorHint = ({ text }: { text: string }): JSX.Element => (
  <p role="alert" className="p-4 text-sm text-red-600">
    {text}
  </p>
);

export default function RequestUnlockModal(): JSX.Element {
  const {
    form,
    stores,
    storeScope,
    listState,
    selectedStore,
    records,
    selected,
    shownCount,
    totalCount,
    isLoadingMore,
    canConfirm,
    onSubmit,
    onListScroll,
    changeEntityType,
    changeStore,
    pickTarget,
    close,
  } = useUnlockRequestForm();

  const STORE_FIELD: Record<UnlockStoreScope, () => JSX.Element | null> = {
    [UNLOCK_STORE_SCOPE.LOADING]: () => null,
    [UNLOCK_STORE_SCOPE.ERROR]: () => null,
    [UNLOCK_STORE_SCOPE.NONE]: () => null,
    [UNLOCK_STORE_SCOPE.SINGLE]: () => (
      <p
        className="text-sm text-muted-foreground"
        data-testid="unlock-single-store"
      >
        {UNLOCK_MODAL_LABELS.SINGLE_STORE(stores[0].label)}
      </p>
    ),
    [UNLOCK_STORE_SCOPE.MANY]: () => (
      <FormField
        control={form.control}
        name="storeCode"
        render={() => (
          <FormItem>
            <FormLabel htmlFor="unlock-store">
              {UNLOCK_MODAL_LABELS.STORE}
            </FormLabel>
            <FormControl>
              <SelectInput
                inputId="unlock-store"
                options={stores}
                value={selectedStore}
                placeholder={UNLOCK_MODAL_LABELS.STORE_PLACEHOLDER}
                isClearable={false}
                onChange={option =>
                  changeStore((option as { value?: string } | null)?.value ?? "")
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  };

  const renderRecord = (record: IUnlockEligibleRecord): JSX.Element => {
    const key: string = unlockRecordKey(record);
    const releasable: boolean = isUnlockReleasable(record);
    const isSelected: boolean = selected ? unlockRecordKey(selected) === key : false;

    return (
      <button
        key={key}
        type="button"
        disabled={!releasable}
        aria-pressed={isSelected}
        onClick={() => pickTarget(record)}
        className={`flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 disabled:opacity-50 ${
          isSelected ? "bg-muted" : ""
        }`}
      >
        <span className="flex flex-col">
          <span className="font-medium">{unlockRecordName(record)}</span>
          <span className="text-muted-foreground">
            {key} - {record.cpf ?? "-"}
          </span>
        </span>
        <SituationCell
          situation={record.situation}
          situationStatus={record.situationStatus}
        />
      </button>
    );
  };

  const LIST_VIEW: Record<UnlockListState, () => JSX.Element> = {
    [UNLOCK_LIST_STATE.STORES_FAILED]: () => (
      <ErrorHint text={UNLOCK_MODAL_LABELS.STORES_FAILED} />
    ),
    [UNLOCK_LIST_STATE.FAILED]: () => (
      <ErrorHint text={UNLOCK_MODAL_LABELS.ELIGIBLE_FAILED} />
    ),
    [UNLOCK_LIST_STATE.NO_STORE]: () => (
      <Hint text={UNLOCK_MODAL_LABELS.NO_STORE} />
    ),
    [UNLOCK_LIST_STATE.PICK_STORE]: () => (
      <Hint text={UNLOCK_MODAL_LABELS.PICK_STORE_FIRST} />
    ),
    [UNLOCK_LIST_STATE.LOADING]: () => (
      <Hint text={UNLOCK_MODAL_LABELS.LOADING} />
    ),
    [UNLOCK_LIST_STATE.EMPTY]: () => (
      <Hint text={UNLOCK_TAB_LABELS.EMPTY_ELIGIBLE} />
    ),
    [UNLOCK_LIST_STATE.ROWS]: () => (
      <>
        {records.map(renderRecord)}
        {isLoadingMore ? <Hint text={UNLOCK_MODAL_LABELS.LOADING_MORE} /> : null}
      </>
    ),
  };

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="w-full space-y-4 px-1"
        data-testid={UNLOCK_MODAL_TEST_ID}
      >
        <FormField
          control={form.control}
          name="entityType"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="unlock-entity-type">
                {UNLOCK_MODAL_LABELS.ENTITY_TYPE}
              </FormLabel>
              <Select
                value={field.value}
                onValueChange={value =>
                  changeEntityType(value as UnlockEntityTypeEnum)
                }
              >
                <FormControl>
                  <SelectTrigger id="unlock-entity-type">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {UNLOCK_ENTITY_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {UNLOCK_ENTITY_LABEL[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {STORE_FIELD[storeScope]()}

        <FormField
          control={form.control}
          name="search"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="unlock-search">
                {UNLOCK_MODAL_LABELS.SEARCH}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="unlock-search"
                  placeholder={UNLOCK_TAB_LABELS.SEARCH_PLACEHOLDER}
                  onKeyDown={keepSearchFromSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="entityCode"
          render={() => (
            <FormItem>
              <div
                className="max-h-56 overflow-y-auto rounded-md border"
                data-testid={UNLOCK_LIST_TEST_ID}
                onScroll={onListScroll}
              >
                {LIST_VIEW[listState]()}
              </div>
              {listState === UNLOCK_LIST_STATE.ROWS && totalCount > shownCount ? (
                <p className="text-xs text-muted-foreground">
                  {UNLOCK_MODAL_LABELS.SHOWING(shownCount, totalCount)}
                </p>
              ) : null}
              {selected ? (
                <p className="text-sm" data-testid={UNLOCK_SELECTED_TEST_ID}>
                  {UNLOCK_MODAL_LABELS.SELECTED(unlockRecordName(selected))}
                </p>
              ) : null}
              <FormMessage />
            </FormItem>
          )}
        />

        {selected && !isUnlockReleasable(selected) ? (
          <p className="text-sm text-red-600">
            {UNLOCK_MODAL_LABELS.NOT_WORKING_HINT}
          </p>
        ) : null}

        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="unlock-comment">
                {UNLOCK_MODAL_LABELS.COMMENT}
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  id="unlock-comment"
                  maxLength={UNLOCK_COMMENT_MAX_LENGTH}
                  placeholder={UNLOCK_MODAL_LABELS.COMMENT_PLACEHOLDER}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={close}>
            {UNLOCK_MODAL_LABELS.CANCEL}
          </Button>
          <Button
            type="submit"
            data-testid={UNLOCK_CONFIRM_TEST_ID}
            disabled={!canConfirm}
          >
            {UNLOCK_MODAL_LABELS.CONFIRM}
          </Button>
        </div>
      </form>
    </Form>
  );
}
